import { useRef, useState } from "react";

export const DEFAULT_LIGHT_DIRECTION = -Math.PI / 5;

let cachedSupport;

function detectLiquidGlassSupport() {
  if (typeof window === "undefined") return false;
  const queuePrototype = globalThis.GPUQueue?.prototype;
  return queuePrototype !== undefined && "copyElementImageToTexture" in queuePrototype;
}

// HTML-in-canvas support never changes during a session, so detect it once
// globally instead of re-running the GPUQueue check in every glass component.
export function isLiquidGlassSupported() {
  if (cachedSupport === undefined) cachedSupport = detectLiquidGlassSupport();
  return cachedSupport;
}

export function useLiquidGlassSupport() {
  const [supported] = useState(isLiquidGlassSupported);
  return supported;
}

// Shared "light follows the pointer" behavior for any GlassContainer.
//
// Raw pointermove events can fire far more often than the display refreshes
// (high-polling-rate mice/trackpads), and each one used to trigger a
// synchronous getBoundingClientRect() — a forced layout read — plus a light
// update. rAF-batching collapses that to at most once per frame.
export function useLiquidGlassLight(defaultLightDirection = DEFAULT_LIGHT_DIRECTION) {
  const glassContainerRef = useRef(null);
  const rafRef = useRef(0);
  const latestRef = useRef(null);

  const applyLight = () => {
    rafRef.current = 0;
    const latest = latestRef.current;
    if (!latest || !glassContainerRef.current) return;
    const { target, clientX } = latest;
    const { left, width } = target.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, (clientX - left) / width));
    glassContainerRef.current.lightDirection = progress * Math.PI - Math.PI / 2;
  };

  const onPointerMove = (event) => {
    latestRef.current = { target: event.currentTarget, clientX: event.clientX };
    if (!rafRef.current) {
      rafRef.current = window.requestAnimationFrame(applyLight);
    }
  };

  const onPointerLeave = () => {
    latestRef.current = null;
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (glassContainerRef.current) {
      glassContainerRef.current.lightDirection = defaultLightDirection;
    }
  };

  return { glassContainerRef, onPointerMove, onPointerLeave };
}
